import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from '@features/tags/components/cells/ActiveBadge';
import { FlagCell } from '@features/priceSeries/components/cells/FlagCell';
import { CropImageCell } from './components/cells/CropImageCell';
import { CropRowActions } from './components/cells/CropRowActions';
import type { Crop } from './schema';

export interface CropRowActionHandlers {
  onEdit: (row: Crop) => void;
}

export function buildCropsColumns(
  actions: CropRowActionHandlers,
): ColDef<Crop>[] {
  return [
    {
      headerName: 'Image',
      width: 70,
      sortable: false,
      filter: false,
      cellRenderer: CropImageCell,
    },
    {
      colId: 'code',
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 140,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: (p: ICellRendererParams<Crop>) =>
        p.data ? (
          <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">
            {p.data.code}
          </code>
        ) : null,
    },
    {
      colId: 'name',
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 140,
    },
    {
      colId: 'displayOrder',
      field: 'displayOrder',
      headerName: 'Order',
      width: 90,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Crop>) =>
        p.data ? (
          <span
            className={
              p.data.displayOrder === null
                ? 'text-foreground-muted'
                : 'text-foreground'
            }
          >
            {p.data.displayOrder ?? '—'}
          </span>
        ) : null,
    },
    {
      field: 'isPreselected',
      headerName: 'Preselected',
      width: 140,
      cellRendererParams: {
        onLabel:
          'Preselected — checked by default on the mobile onboarding screen for new users.',
        offLabel:
          'Not preselected — new users start with this crop unchecked.',
      },
      cellRenderer: FlagCell,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 110,
      cellRenderer: ActiveBadge,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Crop>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<Crop>) =>
        p.data ? <CropRowActions row={p.data} {...actions} /> : null,
    },
  ];
}
